import React, { useState, useRef, useEffect } from 'react';
import { GraduationCap } from 'lucide-react';

// Education qualification options
const EDUCATION_OPTIONS = [
  '10th',
  '12th',
  'Graduate',
  'Post Graduate',
  'Diploma',
  'B.Tech',
  'B.E.',
  'B.Sc',
  'B.Com',
  'B.A.',
  'BBA',
  'BCA',
  'M.Tech',
  'M.E.',
  'M.Sc',
  'M.Com',
  'M.A.',
  'MBA',
  'MCA',
  'Ph.D',
  'ITI',
  'Polytechnic',
];

/**
 * Education Dropdown with Autocomplete (Single Selection)
 * @param {String} selectedEducation - Currently selected education
 * @param {Function} onChange - Callback when education changes
 * @param {Boolean} required - Whether education is required
 * @param {String} placeholder - Placeholder text
 */
const EducationDropdown = ({ 
  selectedEducation = '', 
  onChange, 
  required = false,
  placeholder = 'Select your education qualification...'
}) => {
  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Sync inputValue with selectedEducation when not editing
  useEffect(() => {
    if (!isEditing && selectedEducation) {
      setInputValue(selectedEducation);
    }
  }, [selectedEducation, isEditing]);

  // Filter options based on input
  useEffect(() => {
    const searchValue = isEditing ? inputValue : (inputValue || selectedEducation);
    
    if (searchValue && searchValue.trim()) {
      const filtered = EDUCATION_OPTIONS.filter(option =>
        option.toLowerCase().includes(searchValue.toLowerCase())
      );

      setFilteredOptions(filtered);
    } else {
      setFilteredOptions(EDUCATION_OPTIONS);
    }
  }, [inputValue, selectedEducation, isEditing]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectEducation = (education) => {
    onChange(education);
    setInputValue(education);
    setIsEditing(false);
    setShowSuggestions(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      onChange(inputValue.trim());
      setIsEditing(false);
      setShowSuggestions(false);
    }
  };

  return (
    <div className="w-full">
      {/* Input Field */}
      <div className="relative">
        <GraduationCap className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-600 z-10" size={18} />
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setIsEditing(true);
          }}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            setShowSuggestions(true);
            if (selectedEducation && !isEditing) {
              setIsEditing(true);
              setInputValue('');
            }
          }}
          onBlur={() => {
            // Delay to allow click on suggestion
            setTimeout(() => {
              if (!inputValue && selectedEducation) {
                setInputValue(selectedEducation);
                setIsEditing(false);
              }
            }, 200);
          }}
          placeholder={placeholder}
          required={required}
          autoComplete="off"
          className="w-full p-4 pl-12 bg-white border-b-2 border-black focus:border-yellow-400 focus:outline-none transition-all"
        />

        {/* Autocomplete Dropdown */}
        {showSuggestions && filteredOptions.length > 0 && (
          <div
            ref={dropdownRef}
            className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
          >
            <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
              <p className="text-xs text-gray-600 font-medium">
                {inputValue.trim() ? 'Matching Qualifications' : 'Popular Qualifications'}
              </p>
            </div>
            {filteredOptions.map((option, index) => (
              <button
                key={index}
                type="button"
                onClick={() => handleSelectEducation(option)}
                className="w-full text-left px-4 py-2.5 hover:bg-gray-50 text-sm transition-colors flex items-center gap-2"
              >
                <GraduationCap size={16} className="text-gray-400" />
                <span>{option}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Helper Text */}
      <p className="text-xs text-gray-500 mt-1">
        Select from options        Start typing to see suggestions
      </p>
    </div>
  );
};

export default EducationDropdown;
